import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import Layout from "../../components/Layout/Layout";
import CustomReportBuilder from "./CustomReportBuilder";
import VisitsReportTab from "../../components/Reports/VisitsReportTab";
import { fmtMins, UtilisationBar } from "../../components/Reports/VisitProductivity";
import { Card, Button, Select, Spinner, StatCard } from "../../components/UI";
import {
  dashboardAPI,
  callLogsAPI,
  leadsAPI,
  usersAPI,
  reportsAPI,
  pipelineAPI,
  productsAPI,
} from "../../services/api";
import toast from "react-hot-toast";
import { format } from "date-fns";
import {
  HiOutlineDownload,
  HiOutlineInformationCircle,
  HiOutlineUsers,
  HiOutlineUserAdd,
  HiOutlinePhone,
  HiOutlinePhoneIncoming,
  HiOutlinePhoneMissedCall,
  HiOutlineCalendar,
  HiOutlineClipboardList,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineChartBar,
  HiOutlineLink,
  HiOutlineArrowRight,
  HiOutlineLocationMarker,
} from "react-icons/hi";

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { formatLabel } from '../../utils/formatLabel';

const TABS = [
  { key: "overall", label: "Overall Sales Report" },
  { key: "leads_calls", label: "Lead & Call Report" },
  { key: "team", label: "Team / Employee Performance" },
  { key: "visits", label: "Visits & Productivity" },
  { key: "response_time", label: "Lead Response-Time Analytics" },
  { key: "custom", label: "Custom Report" },
];

const PIE_COLORS = [
  "#3B82F6",
  "#10B981",
  "#8B5CF6",
  "#F97316",
  "#EC4899",
  "#6B7280",
];

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const formatHoursMinutes = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h}h ${m}m`;
};

const CALL_STATUS_STYLES = {
  Connected: "bg-green-100 text-green-700",
  "No Answer": "bg-pink-100 text-pink-700",
  Busy: "bg-purple-100 text-purple-700",
  Rejected: "bg-orange-100 text-orange-700",
  Missed: "bg-pink-100 text-pink-700",
  "Switched Off": "bg-purple-100 text-purple-700",
  "Wrong Number": "bg-red-100 text-red-700",
  "Not Reachable": "bg-amber-100 text-amber-700",
  "Out Of Reach": "bg-orange-100 text-orange-700",
  "Not Connected": "bg-yellow-100 text-yellow-700",
  "Number Busy": "bg-violet-100 text-violet-700",
  "No Response": "bg-indigo-100 text-indigo-700",
  "Call Back": "bg-cyan-100 text-cyan-700",
  "Did Not Connect": "bg-orange-100 text-orange-700",
};

const CALL_STATUS_DISPLAY = {
  connected: 'Connected',
  not_connected: 'Not Connected',
  no_answer: 'No Answer',
  busy: 'Busy',
  rejected: 'Rejected',
  missed: 'Missed',
};

// Ensures any raw enum value or display name is properly formatted
const formatCallStatusName = (name) => {
  if (!name) return 'Unknown';
  // If already a proper display name, return as-is
  if (CALL_STATUS_STYLES[name]) return name;
  // If it's a raw enum key, map it
  if (CALL_STATUS_DISPLAY[name]) return CALL_STATUS_DISPLAY[name];
  // Otherwise title-case it (replace underscores and capitalize)
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const CallStatusBadge = ({ status }) => {
  const displayName = CALL_STATUS_DISPLAY[status] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${CALL_STATUS_STYLES[displayName] || "bg-gray-100 text-gray-700"}`}
    >
      {displayName}
    </span>
  );
};

const LEAD_STAGE_STYLES = {
  "In Progress": "bg-orange-100 text-orange-700",
  Open: "bg-gray-100 text-gray-700",
  Closed: "bg-gray-200 text-gray-700",
  Converted: "bg-green-100 text-green-700",
};

const LeadStageBadge = ({ stage }) => (
  <span
    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${LEAD_STAGE_STYLES[stage] || "bg-gray-100 text-gray-700"}`}
  >
    {stage}
  </span>
);

// Empty State Component
const EmptyState = ({
  message = "No data available",
  description = "Try adjusting your filters or check back later.",
}) => (
  <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
    <HiOutlineInformationCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
    <p className="text-gray-500 font-medium">{message}</p>
    <p className="text-gray-400 text-sm mt-1">{description}</p>
  </div>
);

const Reports = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Chart theme tokens — Recharts draws SVG attributes (stroke/fill) that the
  // global `html.dark` CSS remaps in index.css cannot touch, so the grid,
  // axis ticks, value labels, legend and tooltip colors are picked from the
  // active theme here.
  const chartTheme =
    theme === "dark"
      ? {
          grid: "#2a3158", // grid lines — visible on the #161c36 card surface
          tick: "#8ea1b8", // axis tick text
          label: "#cbd5e1", // value labels (LabelList)
          legend: "#8ea1b8", // legend text
          cursorFill: "rgba(148, 163, 184, 0.08)", // hover highlight band
          tooltipBg: "#161c36",
          tooltipBorder: "#2a3158",
          tooltipText: "#cbd5e1",
          tooltipTitle: "#f1f5f9",
        }
      : {
          grid: "#f0f0f0",
          tick: "#666666",
          label: "#374151",
          legend: "#666666",
          cursorFill: "rgba(0, 0, 0, 0.05)",
          tooltipBg: "#ffffff",
          tooltipBorder: "#e5e7eb",
          tooltipText: "#374151",
          tooltipTitle: "#111827",
        };

  const tooltipContentStyle = {
    backgroundColor: chartTheme.tooltipBg,
    border: `1px solid ${chartTheme.tooltipBorder}`,
    borderRadius: "8px",
    fontSize: "12px",
    color: chartTheme.tooltipText,
    boxShadow:
      theme === "dark"
        ? "0 8px 24px rgba(0, 0, 0, 0.45)"
        : "0 4px 12px rgba(0, 0, 0, 0.08)",
  };
  const tooltipItemStyle = { color: chartTheme.tooltipText };
  const tooltipLabelStyle = { color: chartTheme.tooltipTitle, marginBottom: "4px" };
  const [activeTab, setActiveTab] = useState("overall");
  const [dateRange, setDateRange] = useState("week");
  const [source, setSource] = useState("all");
  const [product, setProduct] = useState("all");
  const [stage, setStage] = useState("all");
  const { isAuthenticated, isInitialized, isLoading: authLoading } = useAuth();
  const [employee, setEmployee] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Get effective date params for API calls (handles custom range)
  const getDateParams = () => {
    if (dateRange === "custom" && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return { range: dateRange };
  };

  // Date query key for cache invalidation
  const dateQueryKey =
    dateRange === "custom" ? `${customStartDate}-${customEndDate}` : dateRange;

  // Check if custom date range is valid (both dates present and To >= From)
  const isCustomDateValid =
    dateRange !== "custom" ||
    (Boolean(customStartDate) &&
      Boolean(customEndDate) &&
      customEndDate >= customStartDate);
  const isQueryEnabled = isAuthenticated && isInitialized && isCustomDateValid;

  // Helper to build common API params including stage
  const getApiParams = () => {
    const params = { ...getDateParams(), source, product };
    if (stage && stage !== "all") params.stage = stage;
    return params;
  };

  // ---- Data fetching ----

  const { isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ["dashboardStats", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getStats(getApiParams());
        return response.data?.data || {};
      } catch (error) {
        console.error("Dashboard stats error:", error);
        return {};
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const { data: stagesData, refetch: refetchStages } = useQuery({
    queryKey: ["pipelineStagesStats", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const params = { ...getDateParams(), source, product };
        if (stage && stage !== "all") params.stage = stage;
        const response = await pipelineAPI.getStats(params);
        return response.data?.data || [];
      } catch (error) {
        console.error("Pipeline stages error:", error);
        return [];
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  // Lead Response-Time Analytics — auto-generated from assigned leads and
  // their first contact calls (assignment → first call).
  const { data: responseTimeData, isLoading: responseTimeLoading } = useQuery({
    queryKey: ["leadResponseTime", dateQueryKey, employee],
    queryFn: async () => {
      try {
        const params = { ...getDateParams() };
        if (employee && employee !== "all") params.employee = employee;
        const response = await reportsAPI.getLeadResponseTime(params);
        return response.data?.data || {};
      } catch (error) {
        console.error("Lead response-time error:", error);
        return {};
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const responseTimeSummary = responseTimeData?.summary || {};
  const responseTimeDistribution = responseTimeData?.distribution || [];
  const responseTimeAgents = responseTimeData?.agents || [];

  const { data: stagesConfig, refetch: refetchStagesConfig } = useQuery({
    queryKey: ["pipelineStagesConfig"],
    queryFn: async () => {
      try {
        const response = await pipelineAPI.getAll();
        return response.data?.data || [];
      } catch (error) {
        console.error("Pipeline config error:", error);
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
    retry: false,
  });

  const stagesWithColors = useMemo(() => {
    if (!stagesData) return [];
    if (!stagesConfig || stagesConfig.length === 0) return stagesData;
    const configMap = stagesConfig.reduce((acc, s) => {
      acc[s.key] = s;
      return acc;
    }, {});
    return stagesData.map((s) => ({
      ...s,
      color: s.color || configMap[s.key]?.color || "#6B7280",
    }));
  }, [stagesData, stagesConfig]);

  const {
    data: leadsCounts,
    isLoading: leadsLoading,
    refetch: refetchLeadsCounts,
  } = useQuery({
    queryKey: ["leadsCounts", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const params = { ...getDateParams(), source, product };
        if (stage && stage !== "all") params.stage = stage;
        const response = await leadsAPI.getCounts(params);
        return response.data?.data || {};
      } catch (error) {
        console.error("Leads counts error:", error);
        return {};
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const {
    data: callStats,
    isLoading: callsLoading,
    refetch: refetchCallStats,
  } = useQuery({
    queryKey: ["callStats", "app", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const params = {
          source: "app",
          ...getDateParams(),
          leadSource: source,
          product,
        };
        if (stage && stage !== "all") params.stage = stage;
        const response = await callLogsAPI.getStatistics(params);
        return response.data?.data || {};
      } catch (error) {
        console.error("Call stats error:", error);
        return {};
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  // Daily activity trend (Leads / Calls / Connected / Converted lines)
  const { data: dailyActivityData = [], refetch: refetchDailyActivity } =
    useQuery({
      queryKey: ["dailyActivity", dateQueryKey, source, product, stage],
      queryFn: async () => {
        try {
          const response = await reportsAPI.getDailyCalls(getApiParams());
          return response.data?.data || [];
        } catch (error) {
          console.error("Daily activity error:", error);
          return [];
        }
      },
      enabled: isQueryEnabled,
      retry: false,
    });

  // Day-wise report — one row per day for the selected range. granularity=day
  // bypasses the endpoint's weekly/monthly auto-aggregation, so a custom
  // range like 1 → 26 Sept lists every single day.
  const { data: dayWiseData = [], refetch: refetchDayWise } = useQuery({
    queryKey: ["dayWiseReport", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getDailyCalls({
          ...getApiParams(),
          granularity: "day",
        });
        return response.data?.data || [];
      } catch (error) {
        console.error("Day-wise report error:", error);
        return [];
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  // Source / product summary table
  const { data: sourceSummary = [], refetch: refetchSourceSummary } = useQuery({
    queryKey: ["sourceSummary", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getLeadSources(getApiParams());
        return response.data?.data || [];
      } catch (error) {
        console.error("Lead sources error:", error);
        return [];
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  // Product-wise performance table
  const { data: productPerformance = [], refetch: refetchProductPerformance } =
    useQuery({
      queryKey: ["productPerformance", dateQueryKey, source, product, stage],
      queryFn: async () => {
        try {
          const response =
            await reportsAPI.getProductPerformance?.(getApiParams());
          return response?.data?.data || [];
        } catch (error) {
          console.error("Product performance error:", error);
          return [];
        }
      },
      enabled: isQueryEnabled,
      retry: false,
    });

  const { data: followUpStats, refetch: refetchFollowUpStats } = useQuery({
    queryKey: ["followUpStats", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getFollowUpStats?.(getApiParams());
        return response?.data?.data || {};
      } catch (error) {
        console.error("Follow-up stats error:", error);
        return {};
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const {
    data: callStatusDistribution = [],
    refetch: refetchCallStatusDistribution,
  } = useQuery({
    queryKey: ["callStatusDistribution", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const params = { ...getDateParams(), source, product };
        if (stage && stage !== "all") params.stage = stage;
        const response = await reportsAPI.getCallStatusDistribution?.(params);
        return response?.data?.data || [];
      } catch (error) {
        console.error("Call status distribution error:", error);
        return [];
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const {
    data: followUpStatusDistribution = [],
    refetch: refetchFollowUpStatusDistribution,
  } = useQuery({
    queryKey: ["followUpStatusDistribution", dateQueryKey, source, product],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getFollowUpStatusDistribution?.({
          ...getDateParams(),
          source,
          product,
        });
        return response?.data?.data || [];
      } catch (error) {
        console.error("Follow-up status distribution error:", error);
        return [];
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const { data: lostReasonData = [], refetch: refetchLostReason } = useQuery({
    queryKey: ["lostReasonAnalysis", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const response =
          await reportsAPI.getLostReasonAnalysis?.(getApiParams());
        return response?.data?.data || [];
      } catch (error) {
        console.error("Lost reason analysis error:", error);
        return [];
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });

  const { data: leadCallDetails = [], refetch: refetchLeadCallDetails } =
    useQuery({
      queryKey: ["leadCallDetails", dateQueryKey, source, product, stage],
      queryFn: async () => {
        try {
          const params = { ...getDateParams(), source, product };
          if (stage && stage !== "all") params.stage = stage;
          const response = await reportsAPI.getLeadCallDetails?.(params);
          return response?.data?.data || [];
        } catch (error) {
          console.error("Lead & call details error:", error);
          return [];
        }
      },
      enabled: isQueryEnabled,
      retry: false,
    });

  const { data: teamPerformance = [], refetch: refetchTeamPerformance } =
    useQuery({
      queryKey: [
        "teamPerformance",
        dateQueryKey,
        employee,
        source,
        product,
        stage,
      ],
      queryFn: async () => {
        try {
          const params = { ...getDateParams(), employee, source, product };
          if (stage && stage !== "all") params.stage = stage;
          const response = await reportsAPI.getTeamPerformance?.(params);
          return response?.data?.data || [];
        } catch (error) {
          console.error("Team performance error:", error);
          return [];
        }
      },
      enabled: isQueryEnabled,
      retry: false,
    });

  const { data: teamSummary, refetch: refetchTeamSummary } = useQuery({
    queryKey: ["teamSummary", dateQueryKey, source, product, stage],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getTeamSummary?.(getApiParams());
        return response?.data?.data || {};
      } catch (error) {
        console.error("Team summary error:", error);
        return {};
      }
    },
    enabled: isQueryEnabled,
    retry: false,
  });
  // Visits & productivity (Visits tab + summary strip on Overall)
  const { data: visitsReport, isLoading: visitsLoading } = useQuery({
    queryKey: ["visitsReport", dateQueryKey, employee],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getVisitsReport({ ...getDateParams(), employee });
        return response?.data?.data || null;
      } catch (error) {
        console.error("Visits report error:", error);
        return null;
      }
    },
    enabled: isQueryEnabled && (activeTab === "visits" || activeTab === "overall"),
    retry: false,
  });

  const isLoading = statsLoading || leadsLoading || callsLoading;

  // ---- Additional filter options ----

  // Fetch products for filter dropdown
  const { data: productsData = [], refetch: refetchProducts } = useQuery({
    queryKey: ["productsList"],
    queryFn: async () => {
      try {
        const response = await productsAPI.getAll({ active: true });
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Fetch users/employees for team filter
  const { data: usersData = [] } = useQuery({
    queryKey: ["usersList"],
    queryFn: async () => {
      try {
        const response = await usersAPI.getAll({ limit: 200 });
        return response.data?.data || response.data?.users || [];
      } catch (error) {
        console.error("Error fetching users:", error);
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Fetch all unique sources dynamically from database
  const { data: sourcesData = [] } = useQuery({
    queryKey: ["uniqueLeadSources"],
    queryFn: async () => {
      try {
        const response = await leadsAPI.getSources();
        return response.data?.data || [];
      } catch (error) {
        console.error("Error fetching sources:", error);
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Source options for dropdown - dynamically loaded from database
  const sourceOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Sources" }];
    if (sourcesData && sourcesData.length > 0) {
      sourcesData.forEach((source) => {
        if (source && source.trim() !== "") {
          options.push({ value: source, label: source });
        }
      });
    }
    return options;
  }, [sourcesData]);

  // Product options for dropdown
  const productOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Products" }];
    if (productsData && productsData.length > 0) {
      productsData.forEach((product) => {
        options.push({ value: product._id, label: product.name });
      });
    }
    return options;
  }, [productsData]);

  // Stage options for dropdown
  const stageOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Stages" }];
    if (stagesConfig && stagesConfig.length > 0) {
      stagesConfig.forEach((stage) => {
        options.push({ value: stage.key, label: stage.name });
      });
    }
    return options;
  }, [stagesConfig]);

  // Employee options for team tab — shows non-admin users only
  const employeeOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Employees" }];
    if (usersData && usersData.length > 0) {
      usersData.forEach((user) => {
        // Exclude admin and super_admin users — only show telecallers and managers
        if (user.role === 'admin' || user.role === 'super_admin') return;
        const id = user._id || user.id;
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email;
        if (id && name) {
          options.push({ value: id, label: name });
        }
      });
    }
    return options;
  }, [usersData]);

  // ---- Derived values ----

  const totalLeads =
    stagesWithColors?.length > 0
      ? stagesWithColors.reduce((sum, s) => sum + (s.leadsCount || 0), 0)
      : leadsCounts
        ? Object.values(leadsCounts).reduce((a, b) => a + (b || 0), 0)
        : 0;

  const convertedCount =
    stagesWithColors?.find((s) => s.key === "converted")?.leadsCount ||
    leadsCounts?.converted ||
    0;

  const lostCount =
    stagesWithColors?.find((s) => s.key === "closed" || s.key === "lost")
      ?.leadsCount ||
    leadsCounts?.closed ||
    0;

  const conversionRate =
    totalLeads > 0 ? ((convertedCount / totalLeads) * 100).toFixed(1) : "0.0";
  const connectionRate =
    callStats?.total > 0
      ? ((callStats.connected / callStats.total) * 100).toFixed(1)
      : "0.0";

  const teamConversionRate = useMemo(() => {
    const totalAssigned = teamPerformance.reduce(
      (a, r) => a + (r.assignedLeads || 0),
      0,
    );
    const totalConverted = teamPerformance.reduce(
      (a, r) => a + (r.converted || 0),
      0,
    );
    return totalAssigned > 0
      ? ((totalConverted / totalAssigned) * 100).toFixed(1)
      : "0.0";
  }, [teamPerformance]);

  // Conversion funnel — built from your pipeline stages, ordered by leadsCount desc
  const funnelData = useMemo(() => {
    const list = stagesWithColors?.length > 0 ? stagesWithColors : [];
    const sorted = [...list].sort(
      (a, b) => (b.leadsCount || 0) - (a.leadsCount || 0),
    );
    const top = sorted[0]?.leadsCount || totalLeads || 1;
    return sorted.map((s) => ({
      name: s.name,
      value: s.leadsCount || 0,
      pct: top > 0 ? (((s.leadsCount || 0) / top) * 100).toFixed(1) : "0.0",
      fill: s.color || "#6B7280",
    }));
  }, [stagesWithColors, totalLeads]);

  const handleExcelExport = async () => {
    try {
      toast.loading("Generating Excel report...");
      const params = { ...getDateParams(), source, product };
      if (stage && stage !== "all") params.stage = stage;

      let response;
      let filename;
      if (activeTab === "visits") {
        response = await reportsAPI.exportVisits({ ...getDateParams(), employee });
        filename = `visits-${dateRange}.xlsx`;
      } else if (activeTab === "team") {
        response = await reportsAPI.exportTeamPerformance(params);
        filename = `team-performance-${dateRange}.xlsx`;
      } else if (activeTab === "leads_calls") {
        response = await reportsAPI.exportCallLogs(params);
        filename = `call-logs-${dateRange}.xlsx`;
      } else {
        response = await reportsAPI.exportLeads(params);
        filename = `leads-${dateRange}.xlsx`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success("Excel report downloaded");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to download Excel report");
      console.error("Excel export error:", error);
    }
  };

  const handleExport = async () => {
    try {
      toast.loading("Generating report...");

      // Build CSV data based on active tab
      const csvRows = [];

      // Header
      csvRows.push(["Reports & Analytics"]);
      csvRows.push([
        `Tab: ${activeTab === "overall" ? "Overall Sales Report" : activeTab === "leads_calls" ? "Lead & Call Report" : activeTab === "visits" ? "Visits & Productivity" : "Team Performance"}`,
      ]);
      csvRows.push([`Date Range: ${dateRange}`]);
      csvRows.push([`Generated: ${new Date().toLocaleString()}`]);
      csvRows.push([]);

      if (activeTab === "overall") {
        // Overall tab
        csvRows.push(["KEY METRICS"]);
        csvRows.push(["Metric", "Value"]);
        csvRows.push(["Total Leads", totalLeads]);
        csvRows.push(["New Leads", leadsCounts?.new || 0]);
        csvRows.push(["Total Calls", callStats?.total || 0]);
        csvRows.push(["Connected Calls", callStats?.connected || 0]);
        csvRows.push(["Not Connected", callStats?.notConnected || 0]);
        csvRows.push(["Conversion Rate", `${conversionRate}%`]);
        csvRows.push(["Connection Rate", `${connectionRate}%`]);
        csvRows.push([]);

        // Source/Product Summary
        if (sourceSummary && sourceSummary.length > 0) {
          csvRows.push(["SOURCE/PRODUCT SUMMARY"]);
          csvRows.push([
            "Source/Product",
            "Leads",
            "Calls",
            "Connected",
            "Converted",
            "Lost",
            "Conversion Rate",
          ]);
          sourceSummary.forEach((row) => {
            csvRows.push([
              row.name,
              row.leads || 0,
              row.calls || 0,
              row.connected || 0,
              row.converted || 0,
              row.lost || 0,
              `${row.leads > 0 ? ((row.converted / row.leads) * 100).toFixed(2) : "0.00"}%`,
            ]);
          });
          csvRows.push([]);
        }

        // Daily Activity
        if (dailyActivityData && dailyActivityData.length > 0) {
          csvRows.push(["DAILY ACTIVITY TREND"]);
          csvRows.push(["Date", "Leads", "Calls", "Connected", "Converted"]);
          dailyActivityData.forEach((item) => {
            csvRows.push([
              item.date || item.fullDate,
              item.leads || item.leadsCreated || 0,
              item.calls || 0,
              item.connected || 0,
              item.converted || 0,
            ]);
          });
          csvRows.push([]);
        }

        // Day-wise report — one row per day for the full selected range
        if (dayWiseData && dayWiseData.length > 0) {
          csvRows.push(["DAY-WISE REPORT"]);
          csvRows.push([
            "Date",
            "Leads Created",
            "Calls",
            "Connected",
            "Not Connected",
            "Call Duration (s)",
            "Follow-ups Completed",
          ]);
          dayWiseData.forEach((row) => {
            csvRows.push([
              row.fullDate || row.date,
              row.leadsCreated || 0,
              row.calls || 0,
              row.connected || 0,
              (row.calls || 0) - (row.connected || 0),
              row.duration || 0,
              row.followUpsCompleted || 0,
            ]);
          });
          csvRows.push([]);
        }
      } else if (activeTab === "leads_calls") {
        // Lead & Call tab
        csvRows.push(["CALL STATISTICS"]);
        csvRows.push(["Metric", "Value"]);
        csvRows.push(["Total Calls", callStats?.total || 0]);
        csvRows.push(["Connected Calls", callStats?.connected || 0]);
        csvRows.push(["Not Connected", callStats?.notConnected || 0]);
        csvRows.push([
          "Avg Duration",
          formatDuration(callStats?.averageDuration || 0),
        ]);
        csvRows.push([]);

        // Call Status Distribution
        if (callStatusDistribution && callStatusDistribution.length > 0) {
          csvRows.push(["CALL STATUS DISTRIBUTION"]);
          csvRows.push(["Status", "Count", "Percentage"]);
          callStatusDistribution.forEach((item) => {
            csvRows.push([
              item.name,
              item.value,
              `${callStats?.total > 0 ? ((item.value / callStats.total) * 100).toFixed(1) : "0.0"}%`,
            ]);
          });
          csvRows.push([]);
        }

        // Lost Reason Analysis
        if (lostReasonData && lostReasonData.length > 0) {
          csvRows.push(["LOST LEADS BY REASON"]);
          csvRows.push(["Reason", "Count"]);
          lostReasonData.forEach((item) => {
            csvRows.push([item.name, item.value]);
          });
          csvRows.push([]);
        }

        // Lead & Call Details
        if (leadCallDetails && leadCallDetails.length > 0) {
          csvRows.push(["LEAD & CALL DETAILS"]);
          csvRows.push([
            "Date & Time",
            "Lead Name",
            "Mobile",
            "Source",
            "Product",
            "Call Status",
            "Duration",
            "Outcome",
            "Follow-up Date",
            "Lead Stage",
          ]);
          leadCallDetails.forEach((row) => {
            csvRows.push([
              row.datetime,
              row.leadName,
              row.mobile,
              row.source,
              row.product,
              row.callStatus,
              row.duration,
              row.outcome,
              row.followUpDate,
              row.leadStage,
            ]);
          });
        }
      } else if (activeTab === "team") {
        // Team tab
        csvRows.push(["TEAM PERFORMANCE"]);
        csvRows.push([
          "Employee",
          "Assigned Leads",
          "Calls Done",
          "Connected",
          "Follow-ups Done",
          "Pending Follow-ups",
          "Demo Scheduled",
          "Converted",
          "Lost",
          "Conversion Rate",
          "Visits Done",
          "Visits Scheduled",
          "Visit Time (min)",
          "Productive Time (min)",
          "Utilisation",
        ]);
        teamPerformance.forEach((row) => {
          csvRows.push([
            row.name,
            row.assignedLeads || 0,
            row.callsDone || 0,
            row.connected || 0,
            row.followUpsDone || 0,
            row.pendingFollowUps || 0,
            row.demoScheduled || 0,
            row.converted || 0,
            row.lost || 0,
            `${row.assignedLeads > 0 ? ((row.converted / row.assignedLeads) * 100).toFixed(2) : "0.00"}%`,
            row.visitsCompleted || 0,
            row.visitsScheduled || 0,
            row.visitMinutes || 0,
            row.productiveMinutes || 0,
            row.utilisation === null || row.utilisation === undefined ? "No attendance" : `${row.utilisation}%`,
          ]);
        });
      } else if (activeTab === "visits" && visitsReport?.summary) {
        const vs = visitsReport.summary;
        csvRows.push(["VISITS SUMMARY"]);
        csvRows.push(["Metric", "Value"]);
        csvRows.push(["Scheduled", vs.scheduled]);
        csvRows.push(["Completed", vs.completed]);
        csvRows.push(["Completion Rate", `${vs.completionRate}%`]);
        csvRows.push(["No Show", vs.noShow]);
        csvRows.push(["Cancelled", vs.cancelled]);
        csvRows.push(["Time on Visits (min)", vs.actualMinutes]);
        csvRows.push(["Avg Visit (min)", vs.avgVisitMinutes]);
        csvRows.push(["Started On Time", `${vs.onTimeRate}%`]);
        csvRows.push(["Repeat Visits", vs.repeatVisits]);
        csvRows.push(["Productive Time (min)", vs.productiveMinutes]);
        csvRows.push(["Avg Utilisation", vs.avgUtilisation === null ? "-" : `${vs.avgUtilisation}%`]);
        csvRows.push([]);
        csvRows.push(["OUTCOMES"]);
        csvRows.push(["Outcome", "Visits"]);
        (visitsReport.outcomes || []).forEach((o) => csvRows.push([o.label, o.count]));
        csvRows.push([]);
        csvRows.push(["EMPLOYEES"]);
        csvRows.push(["Employee", "Completed", "Scheduled", "Missed", "Visit Time (min)", "On Time", "Talk (min)", "Productive (min)", "Utilisation"]);
        (visitsReport.byEmployee || []).forEach((r) =>
          csvRows.push([
            r.name,
            r.visits.completed,
            r.visits.scheduled,
            r.visits.noShow + r.visits.cancelled,
            r.visits.actualMinutes,
            r.visits.onTime + r.visits.late ? `${r.visits.onTimeRate}%` : "-",
            r.productivity.talkMinutes,
            r.productivity.productiveMinutes,
            r.productivity.utilisation === null ? "No attendance" : `${r.productivity.utilisation}%`,
          ])
        );
      }

      // Convert to CSV string
      const csvContent = csvRows
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `reports-${activeTab}-${dateRange}-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success("Report exported successfully");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to export report");
      console.error("Export error:", error);
    }
  };

  if (authLoading || !isInitialized) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="p-4 text-center">
          <p className="text-gray-500">Please log in to view reports</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Reports & Analytics
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Comprehensive insights into your sales and calling performance
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2 lg:justify-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Date Range
              </label>
              <Select
                options={[
                  { value: "week", label: "This Week" },
                  { value: "month", label: "This Month" },
                  { value: "quarter", label: "This Quarter" },
                  { value: "custom", label: "Custom Range" },
                ]}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-32"
              />
            </div>
            {dateRange === "custom" && (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    From
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      setCustomStartDate(newStart);
                      if (customEndDate && newStart > customEndDate) {
                        setCustomEndDate("");
                        toast.error('"To" date cannot be before "From" date');
                      }
                    }}
                    max={customEndDate || undefined}
                    className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      const newEnd = e.target.value;
                      if (customStartDate && newEnd < customStartDate) {
                        toast.error('"To" date cannot be before "From" date');
                        return;
                      }
                      setCustomEndDate(newEnd);
                    }}
                    min={customStartDate || undefined}
                    className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source</label>
              <Select
                options={sourceOptions}
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-32"
              />
            </div>
            {activeTab != "team" && activeTab != "response_time" && activeTab != "visits" && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Product
                </label>
                <Select
                  options={productOptions}
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  className="w-32"
                />
              </div>
            )}
            {activeTab != "team" && activeTab != "response_time" && activeTab != "visits" && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Stage
                </label>
                <Select
                  options={stageOptions}
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="w-32"
                />
              </div>
            )}
            {(activeTab === "team" || activeTab === "response_time" || activeTab === "visits") && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Employee
                </label>
                <Select
                  options={employeeOptions}
                  value={employee}
                  onChange={(e) => setEmployee(e.target.value)}
                  className="w-36"
                />
              </div>
            )}
            {/* Export buttons kept together on one line, same style */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                onClick={handleExport}
              >
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                onClick={handleExcelExport}
              >
                Download Excel
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.key
                  ? "bg-primary-50 text-primary-600 border border-gray-200 border-b-primary-50 -mb-px"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {activeTab === "overall" && (
              <div className="space-y-6">
                {/* Check if there's any data to show */}
                {totalLeads === 0 && !isLoading ? (
                  <Card padding="p-8">
                    <EmptyState
                      message="No data available for selected filters"
                      description="Try adjusting your date range, source, or product filters to see data."
                    />
                  </Card>
                ) : (
                  <>
                    {/* Stat cards row 1 */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                      <StatCard
                        title="Total Leads"
                        value={totalLeads}
                        icon={<HiOutlineUsers className="w-6 h-6 text-white" />}
                        color="primary"
                        trend="up"
                        trendValue="14.2%"
                      />
                      <StatCard
                        title="New Leads"
                        value={leadsCounts?.new_lead || 0}
                        icon={
                          <HiOutlineUserAdd className="w-6 h-6 text-white" />
                        }
                        color="indigo"
                        trend="up"
                        trendValue="18.6%"
                      />
                      <StatCard
                        title="Total Calls"
                        value={callStats?.total || 0}
                        icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
                        color="info"
                        trend="up"
                        trendValue="11.3%"
                      />
                      <StatCard
                        title="Connected Calls"
                        value={callStats?.connected || 0}
                        icon={
                          <HiOutlinePhoneIncoming className="w-6 h-6 text-white" />
                        }
                        color="success"
                        trend="up"
                        trendValue="9.8%"
                      />
                      <StatCard
                        title="Not Connected Calls"
                        value={callStats?.notConnected || 0}
                        icon={
                          <HiOutlinePhoneMissedCall className="w-6 h-6 text-white" />
                        }
                        color="danger"
                        trend="up"
                        trendValue="12.7%"
                      />
                    </div>

                    {/* Stat cards row 2 */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                      <StatCard
                        title="Pending Follow-ups"
                        value={leadsCounts?.follow_up || 0}
                        icon={<HiOutlineClock className="w-6 h-6 text-white" />}
                        color="warning"
                        trend="down"
                        trendValue="6.3%"
                      />
                      <StatCard
                        title="Converted Leads"
                        value={convertedCount}
                        icon={
                          <HiOutlineCheckCircle className="w-6 h-6 text-white" />
                        }
                        color="success"
                        trend="up"
                        trendValue="20.4%"
                      />
                      <StatCard
                        title="Lost Leads"
                        value={lostCount}
                        icon={
                          <HiOutlineXCircle className="w-6 h-6 text-white" />
                        }
                        color="danger"
                        trend="down"
                        trendValue="3.1%"
                      />
                      <StatCard
                        title="Conversion Rate"
                        value={`${conversionRate}%`}
                        icon={
                          <HiOutlineChartBar className="w-6 h-6 text-white" />
                        }
                        color="orange"
                        trend="up"
                        trendValue="3.2%"
                      />
                      <StatCard
                        title="Connection Rate"
                        value={`${connectionRate}%`}
                        icon={<HiOutlineLink className="w-6 h-6 text-white" />}
                        color="primary"
                        trend="up"
                        trendValue="1.8%"
                      />
                    </div>

                    {/* Visits & productivity strip */}
                    {visitsReport?.summary && (visitsReport.summary.scheduled > 0 || visitsReport.summary.productiveMinutes > 0) && (
                      <Card padding="p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <HiOutlineLocationMarker className="w-5 h-5 text-primary-600" />
                            <h3 className="font-semibold text-gray-900">Visits & Productivity</h3>
                          </div>
                          <button
                            type="button"
                            onClick={() => setActiveTab("visits")}
                            className="text-sm font-semibold text-primary-600 hover:text-primary-700"
                          >
                            View details →
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
                          {[
                            ["Visits done", `${visitsReport.summary.completed} / ${visitsReport.summary.scheduled}`],
                            ["Missed", visitsReport.summary.noShow + visitsReport.summary.cancelled],
                            ["Time on visits", fmtMins(visitsReport.summary.actualMinutes)],
                            ["On time", visitsReport.summary.completed ? `${visitsReport.summary.onTimeRate}%` : "—"],
                            ["Productive time", fmtMins(visitsReport.summary.productiveMinutes)],
                            [
                              "Avg utilisation",
                              visitsReport.summary.avgUtilisation === null ? "—" : `${visitsReport.summary.avgUtilisation}%`,
                            ],
                          ].map(([label, value]) => (
                            <div key={label} className="rounded-xl bg-gray-50 px-3 py-2.5">
                              <p className="text-xs text-gray-500">{label}</p>
                              <p className="text-lg font-bold text-gray-900">{value}</p>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Charts row */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* 1. Daily Activity Trend */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          1. Daily Activity Trend
                        </h3>
                        {dailyActivityData.length === 0 ? (
                          <EmptyState
                            message="No activity data"
                            description="No daily activity data available for this period."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={dailyActivityData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                              />
                              <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Line
                                type="monotone"
                                dataKey="leads"
                                name="Leads"
                                stroke="#3B82F6"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="calls"
                                name="Calls"
                                stroke="#8B5CF6"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="connected"
                                name="Connected Calls"
                                stroke="#10B981"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="converted"
                                name="Converted Leads"
                                stroke="#F59E0B"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </Card>

                      {/* 2. Lead Conversion Funnel */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          2. Lead Conversion Funnel
                        </h3>
                        {funnelData.length === 0 ? (
                          <EmptyState
                            message="No funnel data"
                            description="No lead conversion funnel data available."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                              data={funnelData}
                              layout="vertical"
                              margin={{ left: 10, right: 30 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                width={100}
                              />
                              <Tooltip
                                formatter={(value, name) => [
                                  value,
                                  name === "value" ? "Leads" : name,
                                ]}
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                              />
                              <Bar
                                dataKey="value"
                                name="Leads"
                                radius={[0, 4, 4, 0]}
                                barSize={24}
                              >
                                {funnelData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      entry.fill ||
                                      PIE_COLORS[index % PIE_COLORS.length]
                                    }
                                  />
                                ))}
                                <LabelList
                                  dataKey="value"
                                  position="right"
                                  fontSize={11}
                                  fill={chartTheme.label}
                                  fontWeight={500}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>

                      {/* 3. Lead Source Performance */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          3. Lead Source Performance
                        </h3>
                        {sourceSummary.length === 0 ? (
                          <EmptyState
                            message="No source data"
                            description="No lead source performance data available."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={sourceSummary}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                              />
                              <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10, fill: chartTheme.tick }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Bar
                                dataKey="leads"
                                name="Leads"
                                fill="#3B82F6"
                                radius={[3, 3, 0, 0]}
                              />
                              <Bar
                                dataKey="converted"
                                name="Converted"
                                fill="#10B981"
                                radius={[3, 3, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>
                    </div>

                    {/* Tables row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* 4. Source / Product Summary */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          4. Source Summary
                        </h3>
                        {sourceSummary.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No source/product summary data for selected filters."
                          />
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-4 font-medium">
                                    Source
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Leads
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Calls
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Connected
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Converted
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Lost
                                  </th>
                                  <th className="py-2 font-medium">
                                    Conversion Rate
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {sourceSummary.map((row, idx) => (
                                  <tr
                                    key={row.name || idx}
                                    className="border-b border-gray-100"
                                  >
                                    <td className="py-2 pr-4">{row.name}</td>
                                    <td className="py-2 pr-4">
                                      {row.leads || 0}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {row.calls || 0}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {row.connected || 0}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {row.converted || 0}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {row.lost || 0}
                                    </td>
                                    <td className="py-2 font-medium">
                                      {row.leads > 0
                                        ? (
                                            (row.converted / row.leads) *
                                            100
                                          ).toFixed(2)
                                        : "0.00"}
                                      %
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="font-semibold text-primary-600">
                                  <td className="py-2 pr-4">Total</td>
                                  <td className="py-2 pr-4">
                                    {sourceSummary.reduce(
                                      (a, r) => a + (r.leads || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {sourceSummary.reduce(
                                      (a, r) => a + (r.calls || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {sourceSummary.reduce(
                                      (a, r) => a + (r.connected || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {sourceSummary.reduce(
                                      (a, r) => a + (r.converted || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {sourceSummary.reduce(
                                      (a, r) => a + (r.lost || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2">{conversionRate}%</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </Card>

                      {/* 5. Day-wise Report — one row per day */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          5. Day-wise Report
                        </h3>
                        {dayWiseData.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No day-wise data for the selected range and filters."
                          />
                        ) : (
                          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-gray-50 dark:bg-[#1a2140]">
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-4 font-medium">Date</th>
                                  <th className="py-2 pr-4 font-medium">Leads Created</th>
                                  <th className="py-2 pr-4 font-medium">Calls</th>
                                  <th className="py-2 pr-4 font-medium">Connected</th>
                                  <th className="py-2 pr-4 font-medium">Not Connected</th>
                                  <th className="py-2 pr-4 font-medium">Call Duration</th>
                                  <th className="py-2 font-medium">Follow-ups Completed</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayWiseData.map((row, idx) => (
                                  <tr
                                    key={row.fullDate || idx}
                                    className="border-b border-gray-100"
                                  >
                                    <td className="py-2 pr-4 whitespace-nowrap">
                                      {row.fullDate
                                        ? format(new Date(row.fullDate), "d MMM yyyy")
                                        : row.date}
                                    </td>
                                    <td className="py-2 pr-4">{row.leadsCreated || 0}</td>
                                    <td className="py-2 pr-4">{row.calls || 0}</td>
                                    <td className="py-2 pr-4">{row.connected || 0}</td>
                                    <td className="py-2 pr-4">
                                      {(row.calls || 0) - (row.connected || 0)}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {formatHoursMinutes(row.duration || 0)}
                                    </td>
                                    <td className="py-2">{row.followUpsCompleted || 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="font-semibold text-primary-600">
                                  <td className="py-2 pr-4">Total</td>
                                  <td className="py-2 pr-4">
                                    {dayWiseData.reduce((a, r) => a + (r.leadsCreated || 0), 0)}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {dayWiseData.reduce((a, r) => a + (r.calls || 0), 0)}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {dayWiseData.reduce((a, r) => a + (r.connected || 0), 0)}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {dayWiseData.reduce(
                                      (a, r) => a + ((r.calls || 0) - (r.connected || 0)),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {formatHoursMinutes(
                                      dayWiseData.reduce((a, r) => a + (r.duration || 0), 0),
                                    )}
                                  </td>
                                  <td className="py-2">
                                    {dayWiseData.reduce((a, r) => a + (r.followUpsCompleted || 0), 0)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </Card>

                      {/* 6. Product-wise Performance */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          6. Product-wise Performance
                        </h3>
                        {productPerformance.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No product performance data for selected filters."
                          />
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-4 font-medium">
                                    Product
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Leads
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Calls
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Connected
                                  </th>
                                  <th className="py-2 pr-4 font-medium">
                                    Converted
                                  </th>
                                  <th className="py-2 font-medium">
                                    Conversion Rate
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {productPerformance.map((row, idx) => (
                                  <tr
                                    key={row.name || idx}
                                    className="border-b border-gray-100"
                                  >
                                    <td className="py-2 pr-4">{row.name}</td>
                                    <td className="py-2 pr-4">
                                      {row.leads || 0}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {row.calls || 0}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {row.connected || 0}
                                    </td>
                                    <td className="py-2 pr-4 text-green-600 font-medium">
                                      {row.converted || 0}
                                    </td>
                                    <td className="py-2 font-medium">
                                      {row.leads > 0
                                        ? (
                                            (row.converted / row.leads) *
                                            100
                                          ).toFixed(2)
                                        : "0.00"}
                                      %
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="font-semibold text-primary-600">
                                  <td className="py-2 pr-4">Total</td>
                                  <td className="py-2 pr-4">
                                    {productPerformance.reduce(
                                      (a, r) => a + (r.leads || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {productPerformance.reduce(
                                      (a, r) => a + (r.calls || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {productPerformance.reduce(
                                      (a, r) => a + (r.connected || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2 pr-4">
                                    {productPerformance.reduce(
                                      (a, r) => a + (r.converted || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2">{conversionRate}%</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </Card>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "leads_calls" && (
              <div className="space-y-6">
                {/* Check if there's any data */}
                {callStats?.total === 0 &&
                leadCallDetails?.length === 0 &&
                !isLoading ? (
                  <Card padding="p-8">
                    <EmptyState
                      message="No data available"
                      description="No lead or call records found for the selected filters. Try adjusting your date range, source, product, or stage filters."
                    />
                  </Card>
                ) : (
                  <>
                    {/* Stat cards row 1 */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard
                        title="Total Calls"
                        value={callStats?.total || 0}
                        icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
                        color="primary"
                        trend="up"
                        trendValue="11.3%"
                      />
                      <StatCard
                        title="Connected Calls"
                        value={callStats?.connected || 0}
                        icon={
                          <HiOutlinePhoneIncoming className="w-6 h-6 text-white" />
                        }
                        color="success"
                        trend="up"
                        trendValue="9.8%"
                      />
                      <StatCard
                        title="Not Connected Calls"
                        value={callStats?.notConnected || 0}
                        icon={
                          <HiOutlinePhoneMissedCall className="w-6 h-6 text-white" />
                        }
                        color="danger"
                        trend="up"
                        trendValue="12.7%"
                      />
                      <StatCard
                        title="Avg Call Duration"
                        value={formatDuration(callStats?.averageDuration || 0)}
                        icon={<HiOutlineClock className="w-6 h-6 text-white" />}
                        color="indigo"
                        trend="up"
                        trendValue="6.2%"
                      />
                    </div>

                    {/* Stat cards row 2 */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard
                        title="Total Talk Time"
                        value={formatHoursMinutes(
                          callStats?.totalDuration || 0,
                        )}
                        icon={<HiOutlineClock className="w-6 h-6 text-white" />}
                        color="warning"
                        trend="up"
                        trendValue="14.6%"
                      />
                      <StatCard
                        title="Follow-ups Due"
                        value={
                          followUpStats?.due || leadsCounts?.follow_up || 0
                        }
                        icon={
                          <HiOutlineCalendar className="w-6 h-6 text-white" />
                        }
                        color="info"
                        trend="up"
                        trendValue="8.7%"
                      />
                      <StatCard
                        title="Follow-ups Completed"
                        value={followUpStats?.completed || 0}
                        icon={
                          <HiOutlineCheckCircle className="w-6 h-6 text-white" />
                        }
                        color="success"
                        trend="up"
                        trendValue="15.2%"
                      />
                      <StatCard
                        title="Follow-ups Overdue"
                        value={followUpStats?.overdue || 0}
                        icon={<HiOutlineClock className="w-6 h-6 text-white" />}
                        color="danger"
                        trend="up"
                        trendValue="3.1%"
                      />
                    </div>

                    {/* Charts row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                      {/* 1. Daily Calls vs Connected Calls */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          1. Daily Calls vs Connected Calls
                        </h3>
                        {dailyActivityData.length === 0 ? (
                          <EmptyState
                            message="No activity data"
                            description="No daily call data available for this period."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={dailyActivityData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                              />
                              <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Bar
                                dataKey="calls"
                                name="Total Calls"
                                fill="#3B82F6"
                                radius={[3, 3, 0, 0]}
                              />
                              <Bar
                                dataKey="connected"
                                name="Connected Calls"
                                fill="#10B981"
                                radius={[3, 3, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>

                          {/* 2. Call Status Distribution */}
                          <Card padding="p-4 sm:p-6" className="overflow-visible">
                            <h3 className="font-semibold text-gray-900 mb-4">
                              2. Call Status Distribution
                            </h3>
                            {callStatusDistribution.length === 0 ? (
                              <EmptyState
                                message="No call status data"
                                description="No call status distribution available."
                              />
                            ) : (
                              <>
                            <div className="relative" style={{ zIndex: 1 }}>
                              <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                  <Pie
                                    data={callStatusDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={85}
                                    paddingAngle={2}
                                    dataKey="value"
                                    nameKey="name"
                                  >
                                    {callStatusDistribution.map(
                                      (entry, index) => (
                                        <Cell
                                          key={`call-status-${index}`}
                                          fill={entry.color}
                                        />
                                      ),
                                    )}
                                  </Pie>
                                  <Tooltip
                                    formatter={(value, name) => [value, formatCallStatusName(name)]}
                                    contentStyle={tooltipContentStyle}
                                    itemStyle={tooltipItemStyle}
                                    labelStyle={tooltipLabelStyle}
                                    wrapperStyle={{ zIndex: 50 }}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                              {/* Center label — positioned inside donut hole, below tooltip layer */}
                              <div
                                className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                                style={{ top: 0, zIndex: 0 }}
                              >
                                <span className="text-xl font-bold text-gray-900">
                                  {(callStats?.total || 0).toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Total Calls
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 space-y-1.5">
                              {callStatusDistribution.map((item, idx) => (
                                <div
                                  key={`${item.name}-${idx}`}
                                  className="flex items-center justify-between text-xs"
                                >
                                  <span className="flex items-center gap-1.5 text-gray-600">
                                    <span
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: item.color }}
                                    />
                                    {formatCallStatusName(item.name)}
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    {item.value} (
                                    {callStats?.total > 0
                                      ? (
                                          (item.value / callStats.total) *
                                          100
                                        ).toFixed(1)
                                      : "0.0"}
                                    %)
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </Card>

                      {/* 3. Follow-up Status */}
                      <Card padding="p-4 sm:p-6" className="overflow-visible">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          3. Follow-up Status
                        </h3>
                        {followUpStatusDistribution.length === 0 ? (
                          <EmptyState
                            message="No follow-up data"
                            description="No follow-up status distribution available."
                          />
                        ) : (
                          <>
                            <div className="relative" style={{ zIndex: 1 }}>
                              <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                  <Pie
                                    data={followUpStatusDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={85}
                                    paddingAngle={2}
                                    dataKey="value"
                                  >
                                    {followUpStatusDistribution.map(
                                      (entry, index) => (
                                        <Cell
                                          key={`followup-status-${index}`}
                                          fill={entry.color}
                                        />
                                      ),
                                    )}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={{ ...tooltipContentStyle, zIndex: 50 }}
                                    wrapperStyle={{ zIndex: 50 }}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                              {/* Center label — positioned inside donut hole, below tooltip layer */}
                              <div
                                className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                                style={{ top: 0, zIndex: 0 }}
                              >
                                <span className="text-xl font-bold text-gray-900">
                                  {followUpStatusDistribution.reduce(
                                    (a, b) => a + b.value,
                                    0,
                                  )}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Total
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 space-y-1.5">
                              {followUpStatusDistribution.map((item) => {
                                const total = followUpStatusDistribution.reduce(
                                  (a, b) => a + b.value,
                                  0,
                                );
                                return (
                                  <div
                                    key={item.name}
                                    className="flex items-center justify-between text-xs"
                                  >
                                    <span className="flex items-center gap-1.5 text-gray-600">
                                      <span
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: item.color }}
                                      />
                                      {item.name}
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {item.value} (
                                      {total > 0
                                        ? ((item.value / total) * 100).toFixed(
                                            1,
                                          )
                                        : "0.0"}
                                      %)
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </Card>

                      {/* 4. Lost Reason Analysis */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          4. Lost Reason Analysis
                        </h3>
                        {lostReasonData.length === 0 ? (
                          <EmptyState
                            message="No lost leads data"
                            description="No lost leads data for the selected period."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart
                              data={lostReasonData}
                              layout="vertical"
                              margin={{ left: 10 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                              />
                              <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                width={90}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Bar
                                dataKey="value"
                                name="Count"
                                fill="#3B82F6"
                                radius={[0, 3, 3, 0]}
                                barSize={16}
                              >
                                <LabelList
                                  dataKey="value"
                                  position="right"
                                  fontSize={11}
                                  fill={chartTheme.label}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>
                    </div>

                    {/* Lead & Call Details Table */}
                    <Card padding="p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">
                          Lead & Call Details
                        </h3>
                        {leadCallDetails.length > 5 && (
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<HiOutlineArrowRight className="w-4 h-4" />}
                            onClick={() => navigate("/app/leads")}
                          >
                            View All
                          </Button>
                        )}
                      </div>
                      {leadCallDetails.length === 0 ? (
                        <EmptyState
                          message="No lead/call details"
                          description="No lead and call details found for the selected filters."
                        />
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Date & Time
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Lead Name
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Mobile
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Source
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Product
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Call Status
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Duration
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Outcome
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Follow-up Date
                                  </th>
                                  <th className="py-2 font-medium whitespace-nowrap">
                                    Lead Stage
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {leadCallDetails.slice(0, 5).map((row, idx) => (
                                  <tr
                                    key={row.id || idx}
                                    className="border-b border-gray-100"
                                  >
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.datetime}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap font-medium text-gray-900">
                                      {row.leadName}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.mobile}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.source}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.product}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap">
                                      <CallStatusBadge
                                        status={row.callStatus}
                                      />
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.duration}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600 max-w-[150px]">
                                      <span title={row.outcome}>
                                        {row.outcome
                                          ? row.outcome.split(" ").length > 3
                                            ? `${row.outcome.split(" ").slice(0, 3).join(" ")}...`
                                            : row.outcome
                                          : "–"}
                                      </span>
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap">
                                      <span className="text-primary-600 font-medium">
                                        {row.followUpDate || "–"}
                                      </span>
                                    </td>
                                    <td className="py-2.5 whitespace-nowrap">
                                      <LeadStageBadge stage={row.leadStage} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {leadCallDetails.length > 5 && (
                            <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                              <button
                                onClick={() => navigate("/app/leads")}
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 mx-auto"
                              >
                                View all {leadCallDetails.length} leads
                                <HiOutlineArrowRight className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </Card>
                  </>
                )}
              </div>
            )}

            {activeTab === "team" && (
              <div className="space-y-6">
                {/* Check if there's any team data */}
                {teamPerformance.length === 0 && !isLoading ? (
                  <Card padding="p-8">
                    <EmptyState
                      message="No team performance data"
                      description="No team performance data available for the selected filters. Try adjusting your date range or employee filter."
                    />
                  </Card>
                ) : (
                  <>
                    {/* Stat cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                      <StatCard
                        title="Active Users"
                        value={teamSummary?.activeUsers || 0}
                        icon={<HiOutlineUsers className="w-6 h-6 text-white" />}
                        color="primary"
                        trend="up"
                        trendValue="14.3%"
                      />
                      <StatCard
                        title="Assigned Leads"
                        value={teamPerformance.reduce(
                          (a, r) => a + (r.assignedLeads || 0),
                          0,
                        )}
                        icon={
                          <HiOutlineUserAdd className="w-6 h-6 text-white" />
                        }
                        color="indigo"
                        trend="up"
                        trendValue="12.8%"
                      />
                      <StatCard
                        title="Team Calls"
                        value={teamPerformance.reduce(
                          (a, r) => a + (r.callsDone || 0),
                          0,
                        )}
                        icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
                        color="info"
                        trend="up"
                        trendValue="11.3%"
                      />
                      <StatCard
                        title="Connected Calls"
                        value={teamPerformance.reduce(
                          (a, r) => a + (r.connected || 0),
                          0,
                        )}
                        icon={
                          <HiOutlinePhoneIncoming className="w-6 h-6 text-white" />
                        }
                        color="success"
                        trend="up"
                        trendValue="9.8%"
                      />
                      <StatCard
                        title="Team Conversion Rate"
                        value={`${teamConversionRate}%`}
                        icon={
                          <HiOutlineChartBar className="w-6 h-6 text-white" />
                        }
                        color="orange"
                        trend="up"
                        trendValue="3.2%"
                      />
                      <StatCard
                        title="Pending Work"
                        value={teamPerformance.reduce(
                          (a, r) => a + (r.pendingFollowUps || 0),
                          0,
                        )}
                        icon={
                          <HiOutlineClipboardList className="w-6 h-6 text-white" />
                        }
                        color="warning"
                        trend="down"
                        trendValue="6.3%"
                      />
                    </div>

                    {/* Charts row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                      {/* 1. User-wise Calls */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          1. User-wise Calls
                        </h3>
                        {teamPerformance.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No user call data available."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={teamPerformance}
                              layout="vertical"
                              margin={{ left: 10 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                allowDecimals={false}
                              />
                              <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                width={80}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Bar
                                dataKey="callsDone"
                                name="Calls Done"
                                fill="#3B82F6"
                                radius={[0, 3, 3, 0]}
                                barSize={14}
                              >
                                <LabelList
                                  dataKey="callsDone"
                                  position="right"
                                  fontSize={11}
                                  fill={chartTheme.label}
                                  formatter={(v) => Math.round(v)}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>

                      {/* 2. User-wise Connected Calls */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          2. User-wise Connected Calls
                        </h3>
                        {teamPerformance.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No connected calls data available."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={teamPerformance}
                              layout="vertical"
                              margin={{ left: 10 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                allowDecimals={false}
                              />
                              <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                width={80}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Bar
                                dataKey="connected"
                                name="Connected Calls"
                                fill="#10B981"
                                radius={[0, 3, 3, 0]}
                                barSize={14}
                              >
                                <LabelList
                                  dataKey="connected"
                                  position="right"
                                  fontSize={11}
                                  fill={chartTheme.label}
                                  formatter={(v) => Math.round(v)}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>

                      {/* 3. User-wise Conversion */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          3. User-wise Conversion
                        </h3>
                        {teamPerformance.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No conversion data available."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={teamPerformance}
                              layout="vertical"
                              margin={{ left: 10 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                allowDecimals={false}
                              />
                              <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                width={80}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Bar
                                dataKey="converted"
                                name="Converted"
                                fill="#F97316"
                                radius={[0, 3, 3, 0]}
                                barSize={14}
                              >
                                <LabelList
                                  dataKey="converted"
                                  position="right"
                                  fontSize={11}
                                  fill={chartTheme.label}
                                  formatter={(v) => Math.round(v)}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>

                      {/* 4. User-wise Pending Follow-ups */}
                      <Card padding="p-4 sm:p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          4. User-wise Pending Follow-ups
                        </h3>
                        {teamPerformance.length === 0 ? (
                          <EmptyState
                            message="No data available"
                            description="No pending follow-ups data available."
                          />
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={teamPerformance}
                              layout="vertical"
                              margin={{ left: 10 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={chartTheme.grid}
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                allowDecimals={false}
                              />
                              <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: chartTheme.tick }}
                                width={80}
                              />
                              <Tooltip
                                contentStyle={tooltipContentStyle}
                                itemStyle={tooltipItemStyle}
                                labelStyle={tooltipLabelStyle}
                                cursor={{ fill: chartTheme.cursorFill }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.legend }} />
                              <Bar
                                dataKey="pendingFollowUps"
                                name="Pending Follow-ups"
                                fill="#8B5CF6"
                                radius={[0, 3, 3, 0]}
                                barSize={14}
                              >
                                <LabelList
                                  dataKey="pendingFollowUps"
                                  position="right"
                                  fontSize={11}
                                  fill={chartTheme.label}
                                  formatter={(v) => Math.round(v)}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </Card>
                    </div>

                    {/* Team Performance Summary Table */}
                    <Card padding="p-4 sm:p-6">
                      <h3 className="font-semibold text-gray-900 mb-4">
                        Team Performance Summary
                      </h3>
                      {teamPerformance.length === 0 ? (
                        <EmptyState
                          message="No team data available"
                          description="No team performance data found for the selected filters."
                        />
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Employee
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Assigned Leads
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Calls Done
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Connected
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Follow-ups Done
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Pending Follow-ups
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Demo Scheduled
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Converted
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Lost
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Conversion Rate
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Visits Done
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Visit Time
                                  </th>
                                  <th className="py-2 pr-4 font-medium whitespace-nowrap">
                                    Productive Time
                                  </th>
                                  <th className="py-2 font-medium whitespace-nowrap">
                                    Utilisation
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {teamPerformance.map((row, idx) => (
                                  <tr
                                    key={row.id || idx}
                                    className="border-b border-gray-100"
                                  >
                                    <td className="py-2.5 pr-4 whitespace-nowrap font-medium text-gray-900">
                                      {row.name}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.assignedLeads || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.callsDone || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.connected || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.followUpsDone || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.pendingFollowUps || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.demoScheduled || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.converted || 0}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.lost || 0}
                                    </td>
                                    <td className="py-2.5 whitespace-nowrap font-medium text-green-600">
                                      {row.assignedLeads > 0
                                        ? (
                                            (row.converted /
                                              row.assignedLeads) *
                                            100
                                          ).toFixed(2)
                                        : "0.00"}
                                      %
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {row.visitsCompleted || 0}
                                      <span className="text-gray-400"> / {row.visitsScheduled || 0}</span>
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {fmtMins(row.visitMinutes || 0)}
                                    </td>
                                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-600">
                                      {fmtMins(row.productiveMinutes || 0)}
                                    </td>
                                    <td className="py-2.5">
                                      <UtilisationBar value={row.utilisation} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="font-semibold text-primary-600">
                                  <td className="py-2.5 pr-4">Total</td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.assignedLeads || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.callsDone || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.connected || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.followUpsDone || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.pendingFollowUps || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.demoScheduled || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.converted || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamPerformance.reduce(
                                      (a, r) => a + (r.lost || 0),
                                      0,
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {teamConversionRate}%
                                  </td>
                                  <td className="py-2.5 pr-4 whitespace-nowrap">
                                    {teamPerformance.reduce((a, r) => a + (r.visitsCompleted || 0), 0)}
                                    {" / "}
                                    {teamPerformance.reduce((a, r) => a + (r.visitsScheduled || 0), 0)}
                                  </td>
                                  <td className="py-2.5 pr-4 whitespace-nowrap">
                                    {fmtMins(teamPerformance.reduce((a, r) => a + (r.visitMinutes || 0), 0))}
                                  </td>
                                  <td className="py-2.5 pr-4 whitespace-nowrap">
                                    {fmtMins(teamPerformance.reduce((a, r) => a + (r.productiveMinutes || 0), 0))}
                                  </td>
                                  <td className="py-2.5" />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          <div className="flex items-center gap-2 mt-4 text-xs text-gray-500">
                            <HiOutlineInformationCircle className="w-4 h-4 flex-shrink-0" />
                            <span>
                              Use the Employee filter above to view performance
                              for a specific team member.
                            </span>
                          </div>
                        </>
                      )}
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* Lead Response-Time Analytics Tab */}
            {activeTab === "visits" && (
              <VisitsReportTab
                data={visitsReport}
                loading={visitsLoading}
                chart={{ theme: chartTheme, tooltipContentStyle, tooltipItemStyle, tooltipLabelStyle }}
              />
            )}

            {activeTab === "response_time" && (
              <div className="space-y-6">
                {/* Header card */}
                <Card padding="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <HiOutlineClock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
                        Lead Response-Time Analytics
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                        Measures how quickly agents respond to newly assigned
                        leads. Response time = the time between a lead being
                        assigned and the agent's first contact call on it.
                        Generated automatically from your leads and call logs.
                      </p>
                    </div>
                  </div>
                </Card>

                {responseTimeLoading ? (
                  <div className="flex justify-center py-12">
                    <Spinner size="lg" />
                  </div>
                ) : (
                  <>
                    {/* Summary stat cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
                      <StatCard
                        title="Avg Response Time"
                        value={formatHoursMinutes(
                          responseTimeSummary.avgResponseSeconds || 0,
                        )}
                        icon={<HiOutlineClock className="w-5 h-5 text-white" />}
                        color="primary"
                      />
                      <StatCard
                        title="Median Response"
                        value={formatHoursMinutes(
                          responseTimeSummary.medianResponseSeconds || 0,
                        )}
                        icon={<HiOutlineClock className="w-5 h-5 text-white" />}
                        color="info"
                      />
                      <StatCard
                        title="Fastest Response"
                        value={formatHoursMinutes(
                          responseTimeSummary.fastestResponseSeconds || 0,
                        )}
                        icon={
                          <HiOutlineCheckCircle className="w-5 h-5 text-white" />
                        }
                        color="success"
                      />
                      <StatCard
                        title="Slowest Response"
                        value={formatHoursMinutes(
                          responseTimeSummary.slowestResponseSeconds || 0,
                        )}
                        icon={<HiOutlineClock className="w-5 h-5 text-white" />}
                        color="warning"
                      />
                      <StatCard
                        title="Responded Leads"
                        value={`${responseTimeSummary.respondedLeads || 0} / ${responseTimeSummary.totalAssignedLeads || 0}`}
                        icon={<HiOutlineUserAdd className="w-5 h-5 text-white" />}
                        color="indigo"
                      />
                      <StatCard
                        title="Not Responded"
                        value={responseTimeSummary.unrespondedLeads || 0}
                        icon={<HiOutlineXCircle className="w-5 h-5 text-white" />}
                        color="danger"
                      />
                    </div>

                    {/* Distribution chart */}
                    <Card padding="p-4">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4">
                        Response-Time Distribution
                      </h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={responseTimeDistribution}
                          margin={{ top: 24, right: 8, left: -16, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11 }}
                            interval={0}
                            angle={-20}
                            textAnchor="end"
                            height={56}
                          />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip
                            cursor={{ fill: "rgba(0,0,0,0.04)" }}
                            formatter={(value) => [`${value} leads`, "Leads"]}
                          />
                          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                            {responseTimeDistribution.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.color || "#3B82F6"}
                              />
                            ))}
                            <LabelList
                              dataKey="count"
                              position="top"
                              style={{ fontSize: 11, fontWeight: 600 }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>

                    {/* Per-agent response time */}
                    <Card padding="p-4">
                      <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-4">
                        Agent-wise Response Time
                      </h4>
                      {responseTimeAgents.length === 0 ? (
                        <EmptyState
                          message="No response-time data"
                          description="No leads were assigned in the selected period. Try adjusting the date range."
                        />
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Agent</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Role</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Assigned</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Responded</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Response Rate</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Avg Response</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Fastest</th>
                                <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase">Slowest</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {responseTimeAgents.map((agent) => (
                                <tr key={agent._id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                    {agent.name}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 capitalize">
                                    {formatLabel(agent.role)}
                                  </td>
                                  <td className="px-4 py-3 text-gray-900">
                                    {agent.assignedLeads}
                                  </td>
                                  <td className="px-4 py-3 text-gray-900">
                                    {agent.respondedLeads}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        Number(agent.responseRate) >= 80
                                          ? "bg-green-100 text-green-700"
                                          : Number(agent.responseRate) >= 50
                                            ? "bg-amber-100 text-amber-700"
                                            : "bg-red-100 text-red-700"
                                      }`}
                                    >
                                      {agent.responseRate}%
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                                    {formatHoursMinutes(agent.avgResponseSeconds || 0)}
                                  </td>
                                  <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                                    {formatHoursMinutes(agent.fastestResponseSeconds || 0)}
                                  </td>
                                  <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                                    {formatHoursMinutes(agent.slowestResponseSeconds || 0)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* Custom Report Builder Tab */}
            {activeTab === "custom" && (
              <CustomReportBuilder />
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
